import { type ReactNode, useEffect, useRef, useState } from 'react';
import { FLOATING_COMPOSER_SHORTCUT } from '@/components/desktop-shell/floating-composer-chips';
import { matchesShortcut } from '@/lib/platform';

/** Keep one editor mounted, including its draft, attachments and portaled menus. */
export function ExpandableComposer({
  children,
  enabled,
}: {
  children: ReactNode;
  enabled: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const insidePointerEvent = useRef<Event | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setExpanded(false);
    if (!enabled) return;
    let focusCheck: number | null = null;
    const pointerChecks = new Set<number>();
    function onPointerDown(event: PointerEvent) {
      // Listened in the capture phase, so a view that stops the event (an editor, a canvas)
      // still folds the composer. React's own capture handler, which also reaches menus
      // portaled outside the wrapper, runs after this one: decide once the dispatch is over.
      const check = window.setTimeout(() => {
        pointerChecks.delete(check);
        // The reply floating above belongs with the composer: reading it keeps it open.
        const target = event.target;
        const reply = target instanceof Element && target.closest('.desktop-floating-reply');
        if (insidePointerEvent.current !== event && !reply) setExpanded(false);
      });
      pointerChecks.add(check);
    }
    function onWindowBlur() {
      // Clicks inside a frame, a webview or a native browser view never reach this document:
      // it only loses the focus.
      if (focusCheck !== null) window.cancelAnimationFrame(focusCheck);
      focusCheck = window.requestAnimationFrame(() => {
        focusCheck = null;
        const active = document.activeElement;
        if (
          !document.hasFocus() ||
          active instanceof HTMLIFrameElement ||
          active?.tagName === 'WEBVIEW'
        ) {
          setExpanded(false);
        }
      });
    }
    function onKeyDown(event: KeyboardEvent) {
      // ⌘L reaches the composer from the fullscreen view (D58). The browser's own ⌘L (its
      // address bar) is read first, where the keyboard is in it, and stops there.
      if (event.defaultPrevented || !matchesShortcut(event, FLOATING_COMPOSER_SHORTCUT)) return;
      const editor = wrapperRef.current?.querySelector<HTMLElement>('[contenteditable="true"]');
      if (!editor) return;
      event.preventDefault();
      editor.focus();
      setExpanded(true);
    }
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      if (focusCheck !== null) window.cancelAnimationFrame(focusCheck);
      for (const check of pointerChecks) window.clearTimeout(check);
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [enabled]);

  return (
    <div
      className={enabled ? 'desktop-expandable-composer' : 'contents'}
      data-expanded={enabled ? expanded : undefined}
      onFocusCapture={() => enabled && setExpanded(true)}
      ref={wrapperRef}
      onPointerDownCapture={(event) => {
        insidePointerEvent.current = event.nativeEvent;
        if (enabled) setExpanded(true);
      }}
    >
      {children}
    </div>
  );
}
